import type { LeagueRepository } from "@/modules/leagues/repositories/LeagueRepository";
import type { MatchRepository } from "@/modules/leagues/repositories/MatchRepository";
import type { FeedRepository } from "@/modules/leagues/repositories/FeedRepository";
import type { StatisticsRepository } from "@/modules/leagues/repositories/StatisticsRepository";
import type { LeagueAdminRepository } from "@/modules/leagues/repositories/LeagueAdminRepository";
import type { CompetitionRepository } from "@/modules/leagues/repositories/CompetitionRepository";
import type { RoundBonusRepository } from "@/modules/leagues/repositories/RoundBonusRepository";
import type { BonusQuestionsRepository } from "@/modules/leagues/repositories/BonusQuestionsRepository";
import type { DuelRepository } from "@/modules/leagues/repositories/DuelRepository";
import { MockLeagueRepository } from "@/modules/leagues/repositories/mock/MockLeagueRepository";
import { FirestoreLeagueRepository } from "@/modules/leagues/repositories/firestore/FirestoreLeagueRepository";
import { MockMatchRepository } from "@/modules/leagues/repositories/mock/MockMatchRepository";
import { MockFeedRepository } from "@/modules/leagues/repositories/mock/MockFeedRepository";
import { FirestoreFeedRepository } from "@/modules/leagues/repositories/firestore/FirestoreFeedRepository";
import { MockStatisticsRepository } from "@/modules/leagues/repositories/mock/MockStatisticsRepository";
import { FirestoreStatisticsRepository } from "@/modules/leagues/repositories/firestore/FirestoreStatisticsRepository";
import { MockLeagueAdminRepository } from "@/modules/leagues/repositories/mock/MockLeagueAdminRepository";
import { FirestoreLeagueAdminRepository } from "@/modules/leagues/repositories/firestore/FirestoreLeagueAdminRepository";
import { MockCompetitionRepository } from "@/modules/leagues/repositories/mock/MockCompetitionRepository";
import { MockRoundBonusRepository } from "@/modules/leagues/repositories/mock/MockRoundBonusRepository";
import { FirestoreRoundBonusRepository } from "@/modules/leagues/repositories/firestore/FirestoreRoundBonusRepository";
import { MockBonusQuestionsRepository } from "@/modules/leagues/repositories/mock/MockBonusQuestionsRepository";
import { FirestoreBonusQuestionsRepository } from "@/modules/leagues/repositories/firestore/FirestoreBonusQuestionsRepository";
import { MockDuelRepository } from "@/modules/leagues/repositories/mock/MockDuelRepository";
import { FirestoreDuelRepository } from "@/modules/leagues/repositories/firestore/FirestoreDuelRepository";
import { FootballDataMatchRepository } from "@/modules/leagues/repositories/football/FootballDataMatchRepository";
import { FootballDataCompetitionRepository } from "@/modules/leagues/repositories/football/FootballDataCompetitionRepository";
import { FirestoreCachedMatchRepository } from "@/modules/leagues/repositories/firestore/FirestoreCachedMatchRepository";
import { CompositeMatchRepository } from "@/modules/leagues/repositories/CompositeMatchRepository";
import { CompositeCompetitionRepository } from "@/modules/leagues/repositories/CompositeCompetitionRepository";

/**
 * Único ponto de decisão "mock vs. real" do app. Nenhum hook, página ou
 * componente deve saber qual implementação está em uso — todos dependem
 * apenas das interfaces (LeagueRepository, MatchRepository, ...).
 *
 * USE_MOCK_DATA = true  → dados fictícios realistas (MockMatchRepository, MockCompetitionRepository)
 * USE_MOCK_DATA = false → Football Data API real (FootballDataMatchRepository, FootballDataCompetitionRepository)
 *
 * Ligas, Feed, Estatísticas e Admin continuam em Mock nesta etapa — só
 * Football/Matches e Competições foram conectados à API real, conforme o
 * escopo desta etapa ("integração real de dados de futebol").
 * FirestoreMatchRepository já existe (ver repositories/firestore/) e fica
 * pronto para a próxima etapa (ScoringEngine + sincronização automática),
 * sem estar plugado aqui ainda.
 */
/**
 * false = experiência real (Firestore + Football Data API).
 * true  = volta tudo pra mock, só pra teste/desenvolvimento local.
 *
 * ATENÇÃO — pré-requisitos pra false funcionar de verdade:
 * 1) firestore.rules precisa estar deployado com as regras de `groups`
 *    (rodar `firebase deploy --only firestore:rules` ou colar no Console).
 * 2) VITE_FOOTBALL_DATA_API_KEY precisa ser uma chave válida com acesso
 *    às competições que você for usar.
 * Nenhuma dessas duas coisas foi testada ao vivo neste ambiente (sandbox
 * sem internet) — teste localmente antes de confiar em produção.
 */
export const USE_MOCK_DATA = false;

export const leagueRepository: LeagueRepository = USE_MOCK_DATA
  ? new MockLeagueRepository()
  : new FirestoreLeagueRepository();

export const feedRepository: FeedRepository = USE_MOCK_DATA ? new MockFeedRepository() : new FirestoreFeedRepository();
export const statisticsRepository: StatisticsRepository = USE_MOCK_DATA
  ? new MockStatisticsRepository()
  : new FirestoreStatisticsRepository();
export const leagueAdminRepository: LeagueAdminRepository = USE_MOCK_DATA
  ? new MockLeagueAdminRepository()
  : new FirestoreLeagueAdminRepository();
export const roundBonusRepository: RoundBonusRepository = USE_MOCK_DATA
  ? new MockRoundBonusRepository()
  : new FirestoreRoundBonusRepository();

export const bonusQuestionsRepository: BonusQuestionsRepository = USE_MOCK_DATA
  ? new MockBonusQuestionsRepository()
  : new FirestoreBonusQuestionsRepository();

export const duelRepository: DuelRepository = USE_MOCK_DATA ? new MockDuelRepository() : new FirestoreDuelRepository();

export const matchRepository: MatchRepository = USE_MOCK_DATA
  ? new MockMatchRepository()
  : new CompositeMatchRepository(
      new FootballDataMatchRepository(leagueRepository),
      new FirestoreCachedMatchRepository(leagueRepository),
      async (leagueId) => (await leagueRepository.getLeagueById(leagueId))?.competitionId ?? null
    );

export const competitionRepository: CompetitionRepository = USE_MOCK_DATA
  ? new MockCompetitionRepository()
  : new CompositeCompetitionRepository(new FootballDataCompetitionRepository());
